import { useEffect, useState } from 'react';
import { Dialog } from '@/components/ui/Dialog';
import { downloadBlob } from '@/lib/utils/download.utils';
import { useResourceDetail } from '../hooks/useResourceDetail';
import { resolveFilePreviewKind, resourceFileName } from '../resource-file';
import { RESOURCES_STRINGS } from '../constants/resources-strings';
import { ResourcePartnerCredit } from './ResourcePartnerCredit';
import type { ResourceItemDto } from '@/lib/types/resources.types';

export interface ResourceDetailDialogProps {
  /** The item to show. `null` closes the dialog. */
  item: ResourceItemDto | null;
  onClose: () => void;
}

type SaveState = 'idle' | 'saving' | 'error';

/**
 * Resource detail dialog (MYN-50/53/100 fix F-5 / F-6).
 *
 * Opened by a file-backed card's "View Details" action. The file preview fills
 * the dialog, with Save beneath it.
 *
 * A PDF renders in the browser's own viewer, which already provides the page
 * thumbnails on the left and the selected page on the right - so the preview
 * is given the full width rather than being squeezed beside a file list of a
 * single row. An item carries exactly one file (fix spec D-3), so naming that
 * file in a list panel told the member nothing the dialog heading did not.
 *
 * The presigned download URL is fetched lazily per open and never persisted:
 * it is short-lived, so it must be fresh each time the dialog opens.
 */
export function ResourceDetailDialog({ item, onClose }: ResourceDetailDialogProps) {
  const { data: detail, isLoading, isError } = useResourceDetail(item?.id ?? null);
  const [saveState, setSaveState] = useState<SaveState>('idle');

  // Reset the save state per item, so an error from one file does not persist
  // into the next dialog the member opens.
  useEffect(() => {
    setSaveState('idle');
  }, [item?.id]);

  if (!item) return null;

  // previewUrl: inline URL for the in-page viewer (PDF/PNG/JPEG); always
  // generated regardless of isDownloadable (MYN-192).
  // downloadUrl: null when the resource is download-restricted for this caller;
  // drives Save button visibility per FR-79.
  // hasFile: true when a file is attached at all (fileUrl non-null), so the
  // preview area shows content (or "unavailable") rather than "no file".
  const previewUrl = detail?.previewUrl ?? null;
  const downloadUrl = detail?.downloadUrl ?? null;
  const hasFile = (detail?.fileUrl ?? item.fileUrl) !== null;
  const previewKind = resolveFilePreviewKind(detail?.fileMimeType ?? item.fileMimeType);
  const fileName = resourceFileName(
    item.title,
    detail?.fileUrl ?? item.fileUrl,
    detail?.fileMimeType ?? item.fileMimeType,
  );

  async function handleSave(): Promise<void> {
    if (!downloadUrl) return;
    setSaveState('saving');
    try {
      const response = await fetch(downloadUrl);
      if (!response.ok) {
        throw new Error(`Download failed with status ${response.status}`);
      }
      downloadBlob(await response.blob(), fileName);
      setSaveState('idle');
    } catch (error) {
      // Surfaced in the dialog rather than swallowed - the member clicked Save
      // and must be told it did not happen.
      // eslint-disable-next-line no-console
      console.error('Resource file download failed', error);
      setSaveState('error');
    }
  }

  return (
    <Dialog
      open
      onOpenChange={(next) => {
        if (!next) onClose();
      }}
      title={item.title}
      size="2xl"
    >
      <div className="flex flex-col gap-3">
        <div className="flex h-[60vh] min-h-[20rem] items-center justify-center overflow-hidden rounded-md border border-card-border bg-[#F7F9FB]">
          {isLoading && (
            <p className="text-sm text-[#A0AEBC]">{RESOURCES_STRINGS.DIALOG.LOADING_PREVIEW}</p>
          )}

          {!isLoading && isError && (
            <p role="alert" className="px-6 text-center text-sm text-danger">
              {RESOURCES_STRINGS.ERRORS.DETAIL_LOAD_FAILED}
            </p>
          )}

          {!isLoading && !isError && !hasFile && (
            <p className="px-6 text-center text-sm text-[#A0AEBC]">
              {RESOURCES_STRINGS.DIALOG.NO_FILE}
            </p>
          )}

          {!isLoading && !isError && hasFile && previewKind === 'pdf' && (
            <iframe
              src={previewUrl ?? undefined}
              title={RESOURCES_STRINGS.DIALOG.PREVIEW_TITLE(item.title)}
              className="h-full w-full"
            />
          )}

          {!isLoading && !isError && hasFile && previewKind === 'image' && (
            <img
              src={previewUrl ?? undefined}
              alt={RESOURCES_STRINGS.DIALOG.PREVIEW_TITLE(item.title)}
              // Load as a CORS request. Without this the browser caches a
              // non-CORS response for this URL, and Save's `fetch` of the
              // same URL reuses that entry and fails the CORS check - which
              // is why images failed to save while PDFs (loaded in an
              // iframe, a separate navigation) saved fine.
              crossOrigin="anonymous"
              className="max-h-full max-w-full object-contain"
            />
          )}

          {!isLoading && !isError && hasFile && previewKind === 'none' && (
            <div className="px-6 text-center">
              <p className="text-sm font-medium text-[#001928]">
                {RESOURCES_STRINGS.DIALOG.PREVIEW_UNAVAILABLE}
              </p>
              {downloadUrl !== null && (
                <p className="mt-1 text-[13px] text-[#A0AEBC]">
                  {RESOURCES_STRINGS.DIALOG.PREVIEW_UNAVAILABLE_HINT}
                </p>
              )}
            </div>
          )}
        </div>

        {/* Partner credit (FR-278). Read from the DETAIL response, not the
            list item the dialog was opened from: the detail read resolves the
            summary, and the two can differ if the partner stopped being
            eligible since the list loaded. */}
        {detail?.trustedPartner && (
          <ResourcePartnerCredit partner={detail.trustedPartner} size="md" />
        )}

        {saveState === 'error' && (
          <p role="alert" className="text-sm text-danger">
            {RESOURCES_STRINGS.ERRORS.DOWNLOAD_FAILED}
          </p>
        )}

        {/* Save button: only when downloadUrl is non-null (MYN-192, FR-79).
            A null downloadUrl means either no file or an admin-restricted
            resource — in both cases Save is absent, not disabled. */}
        {downloadUrl !== null && (
          <div className="flex justify-end">
            <button
              type="button"
              onClick={() => void handleSave()}
              disabled={saveState === 'saving'}
              className="text-[14px] font-semibold text-[#4DBA72] hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#4DBA72] disabled:cursor-not-allowed disabled:opacity-60 disabled:no-underline disabled:hover:no-underline"
            >
              {saveState === 'saving'
                ? RESOURCES_STRINGS.DIALOG.SAVING
                : RESOURCES_STRINGS.DIALOG.SAVE}
            </button>
          </div>
        )}
      </div>
    </Dialog>
  );
}
